import * as Sentry from '@sentry/browser';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';

import { PageProgressNode, ProgramProgressNode, UserInput } from 'src/generated/webapp_gql';

import { appClient } from 'src/config/apollo';
import { FinishPageDocument } from 'src/modules/newPrograms/documents/finishPageMutation.generated';
import { FinishProgramDocument } from 'src/modules/newPrograms/documents/finishProgramMutation.generated';
import { StartPageDocument } from 'src/modules/newPrograms/documents/startPageMutation.generated';
import { StartProgramDocument } from 'src/modules/newPrograms/documents/startProgramMutation.generated';
import { selectAnswerOption } from '../effects';

export class ProgramService {
    public apolloClient: ApolloClient<NormalizedCacheObject>;

    constructor(apolloClient: ApolloClient<NormalizedCacheObject> = appClient) {
        this.apolloClient = apolloClient;
    }

    public async startProgram(programUuid: string): Promise<ProgramProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: StartProgramDocument,
                variables: { programUuid },
            })
            .then(result => result.data.programProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }

    public async finishProgram(programUuid: string): Promise<ProgramProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: FinishProgramDocument,
                variables: { programUuid },
            })
            .then(result => result.data.finishProgram.programProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }

    public async startPage(pageUuid: string): Promise<PageProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: StartPageDocument,
                variables: { pageUuid },
            })
            .then(result => result.data.pageProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }

    public selectPageAnswer(
        pageId: string,
        answerIndex: number,
        isSelected?: boolean,
        customOptionValue?: string,
    ): boolean {
        return selectAnswerOption(this.apolloClient, {
            pageId,
            answerIndex,
            isSelected,
            customOptionValue,
        });
    }

    public async finishPage(pageUuid: string, answers: UserInput[]): Promise<PageProgressNode[]> {
        return this.apolloClient
            .mutate({
                mutation: FinishPageDocument,
                variables: {
                    pageUuid,
                    input: JSON.stringify(answers),
                },
            })
            .then(result => result.data.pageProgress)
            .catch(error => {
                Sentry.captureException(error);
            });
    }
}
